/**
 * Hazard Zone Renderer (L3-LINGERING-HAZARDS)
 *
 * Renders persistent battlefield effects that remain after abilities are used.
 * Tier 1: Visual-only (no gameplay effect, purely cosmetic).
 *
 * Fire: Flickering orange/red ground effect
 * Ice: Blue crystalline patches
 * Poison: Green bubbling/mist effect
 * Lightning: Crackling ground sparks
 */

import * as PIXI from 'pixi.js';
import { getElementConfig, type ElementType } from './effects/element-effect-configs.js';

/**
 * Hazard duration per element (in milliseconds)
 */
const HAZARD_DURATIONS: Record<string, number> = {
  fire: 6000,
  ice: 8000,
  poison: 10000,
  lightning: 3000,
  dark: 7000,
  holy: 5000,
  default: 5000
};

/**
 * Active hazard tracking
 */
interface ActiveHazard {
  id: string;
  position: { x: number; y: number };
  element: string;
  graphic: PIXI.Graphics;
  createdAt: number;
  duration: number;
  baseAlpha: number;
}

/**
 * HazardZoneRenderer - Renders lingering ground effects after abilities
 */
export class HazardZoneRenderer {
  private hazards: Map<string, ActiveHazard> = new Map();
  private container: PIXI.Container;
  private nextId = 0;

  constructor(container: PIXI.Container) {
    this.container = container;
  }

  /**
   * Spawn a hazard at the given position
   */
  public spawnHazard(
    position: { x: number; y: number },
    element: string,
    duration?: number
  ): string {
    const id = `hazard-${this.nextId++}`;
    const config = getElementConfig(element as ElementType);
    const hazardDuration = duration ?? HAZARD_DURATIONS[element] ?? HAZARD_DURATIONS.default;

    const graphic = new PIXI.Graphics();
    this.drawHazard(graphic, element, config);
    graphic.position.set(position.x, position.y);
    graphic.blendMode = 'add' as PIXI.BLEND_MODES;

    this.container.addChild(graphic);

    const baseAlpha = 0.4;
    graphic.alpha = baseAlpha;

    this.hazards.set(id, {
      id,
      position,
      element,
      graphic,
      createdAt: Date.now(),
      duration: hazardDuration,
      baseAlpha
    });

    console.log(`[HazardZoneRenderer] Spawned ${element} hazard at (${position.x}, ${position.y}), duration: ${hazardDuration}ms`);

    return id;
  }

  /**
   * Draw hazard graphic based on element
   */
  private drawHazard(graphic: PIXI.Graphics, element: string, config: ReturnType<typeof getElementConfig>): void {
    const radius = 30;

    switch (element) {
      case 'fire':
        // Flickering fire pool with multiple circles
        graphic.circle(0, 0, radius);
        graphic.fill({ color: config.primaryColor, alpha: 0.4 });
        graphic.circle(3, -2, radius * 0.7);
        graphic.fill({ color: config.secondaryColor, alpha: 0.3 });
        graphic.circle(-2, 3, radius * 0.5);
        graphic.fill({ color: 0xFF8C00, alpha: 0.2 });
        break;

      case 'ice':
        // Crystalline ice patch with angular edges
        graphic.circle(0, 0, radius);
        graphic.fill({ color: config.primaryColor, alpha: 0.3 });
        // Add crystal highlights
        for (let i = 0; i < 6; i++) {
          const angle = (i / 6) * Math.PI * 2;
          const x = Math.cos(angle) * (radius * 0.6);
          const y = Math.sin(angle) * (radius * 0.6);
          graphic.circle(x, y, 5);
          graphic.fill({ color: 0xFFFFFF, alpha: 0.4 });
        }
        break;

      case 'poison':
        // Bubbling poison pool
        graphic.circle(0, 0, radius);
        graphic.fill({ color: config.primaryColor, alpha: 0.35 });
        // Add bubbles
        for (let i = 0; i < 5; i++) {
          const bx = (Math.random() - 0.5) * radius * 1.5;
          const by = (Math.random() - 0.5) * radius * 1.5;
          const bsize = 3 + Math.random() * 4;
          graphic.circle(bx, by, bsize);
          graphic.fill({ color: 0x00FF00, alpha: 0.5 });
        }
        break;

      case 'lightning':
        // Crackling electrical ground
        graphic.circle(0, 0, radius * 0.8);
        graphic.fill({ color: config.primaryColor, alpha: 0.25 });
        // Add spark lines
        for (let i = 0; i < 4; i++) {
          const angle = (i / 4) * Math.PI * 2 + Math.random() * 0.5;
          graphic.moveTo(0, 0);
          graphic.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
          graphic.stroke({ width: 2, color: 0xFFFF00, alpha: 0.6 });
        }
        break;

      case 'dark':
        // Swirling dark energy
        graphic.circle(0, 0, radius);
        graphic.fill({ color: 0x4B0082, alpha: 0.4 });
        graphic.circle(0, 0, radius * 0.6);
        graphic.fill({ color: 0x000000, alpha: 0.3 });
        break;

      case 'holy':
        // Gentle glowing aura
        graphic.circle(0, 0, radius);
        graphic.fill({ color: 0xFFFFF0, alpha: 0.3 });
        graphic.circle(0, 0, radius * 0.5);
        graphic.fill({ color: 0xFFD700, alpha: 0.2 });
        break;

      default:
        // Generic hazard
        graphic.circle(0, 0, radius);
        graphic.fill({ color: config.primaryColor, alpha: 0.3 });
    }
  }

  /**
   * Update all hazards (called each frame)
   */
  public update(): void {
    const now = Date.now();

    for (const [id, hazard] of this.hazards) {
      const elapsed = now - hazard.createdAt;
      const remaining = hazard.duration - elapsed;

      if (remaining <= 0) {
        // Remove expired hazard
        this.container.removeChild(hazard.graphic);
        hazard.graphic.destroy();
        this.hazards.delete(id);
        continue;
      }

      // Fade out in final 1 second
      if (remaining < 1000) {
        hazard.graphic.alpha = (remaining / 1000) * hazard.baseAlpha;
      }

      // Element-specific animation
      this.animateHazard(hazard, elapsed);
    }
  }

  /**
   * Animate hazard based on element type
   */
  private animateHazard(hazard: ActiveHazard, elapsed: number): void {
    switch (hazard.element) {
      case 'fire':
        // Flicker effect
        const flicker = 0.9 + Math.sin(elapsed / 80) * 0.1 + Math.sin(elapsed / 120) * 0.05;
        hazard.graphic.scale.set(flicker);
        break;

      case 'ice':
        // Slow shimmer
        const shimmer = 1 + Math.sin(elapsed / 500) * 0.03;
        hazard.graphic.scale.set(shimmer);
        break;

      case 'poison':
        // Bubbling pulse
        const bubble = 1 + Math.sin(elapsed / 300) * 0.05;
        hazard.graphic.scale.set(bubble);
        break;

      case 'lightning':
        // Rapid sparking
        const spark = 0.95 + Math.random() * 0.1;
        hazard.graphic.scale.set(spark);
        hazard.graphic.alpha = hazard.baseAlpha * (0.7 + Math.random() * 0.3);
        break;

      case 'dark':
        // Slow swirl
        hazard.graphic.rotation = elapsed / 3000;
        const swirl = 1 + Math.sin(elapsed / 400) * 0.04;
        hazard.graphic.scale.set(swirl);
        break;

      case 'holy':
        // Gentle glow
        const glow = 1 + Math.sin(elapsed / 600) * 0.03;
        hazard.graphic.scale.set(glow);
        break;

      default:
        // Simple pulse
        const pulse = 1 + Math.sin(elapsed / 200) * 0.05;
        hazard.graphic.scale.set(pulse);
    }
  }

  /**
   * Get count of active hazards
   */
  public getActiveCount(): number {
    return this.hazards.size;
  }

  /**
   * Clear all hazards
   */
  public clear(): void {
    for (const hazard of this.hazards.values()) {
      this.container.removeChild(hazard.graphic);
      hazard.graphic.destroy();
    }
    this.hazards.clear();
  }
}
